import { useEffect, useState } from 'react';
import {
    ensureINSToken,
    hasInteractiveServiceSession,
    interactiveServiceFetch,
} from '../../services/interactiveServiceFetch';
import { getImpersonationFromQuery } from '../../utils/url';
import { DEFAULT_SUGGESTION_ICON, PROMPT_SUGGESTIONS_URL } from '../aiPromptSuggestions';
import { PROMPT_CARDS_PER_TAB } from '../promptLibrary';

const PREFETCH_IDLE_TIMEOUT_MS = 3000;

const isImpersonating = () => Boolean(getImpersonationFromQuery('user') || getImpersonationFromQuery('customer_id'));

const trimString = value => (typeof value === 'string' ? value.trim() : '');

let pendingRequest = null;
let settledResult;

export const resetPromptSuggestionsCache = () => {
    pendingRequest = null;
    settledResult = undefined;
};

const normalizeSuggestion = item => {
    const text = trimString(item?.text);
    if (!text) return null;

    return {
        text,
        label: trimString(item.label) || text,
        tag: trimString(item.tag),
        icon: trimString(item.icon) || DEFAULT_SUGGESTION_ICON,
    };
};

const parseSuggestions = data => {
    if (!data?.personalized || !Array.isArray(data.suggestions) || data.suggestions.length !== PROMPT_CARDS_PER_TAB) {
        return null;
    }

    const suggestions = data.suggestions.map(normalizeSuggestion);
    return suggestions.every(Boolean) ? suggestions : null;
};

const requestSuggestions = async () => {
    if (isImpersonating()) {
        const refreshResult = await ensureINSToken();
        if (!refreshResult.ok) throw new Error('Interactive-service token refresh failed');
    }

    const response = await interactiveServiceFetch(PROMPT_SUGGESTIONS_URL);
    if (!response.ok) throw new Error(`Prompt suggestions request failed with ${response.status}`);
    return parseSuggestions(await response.json());
};

const startRequest = () => {
    if (pendingRequest) return pendingRequest;

    pendingRequest = requestSuggestions()
        .then(result => {
            settledResult = result;
            return result;
        })
        .catch(() => {
            pendingRequest = null;
            return null;
        });

    return pendingRequest;
};

export const prefetchPromptSuggestions = () => {
    if (pendingRequest || !hasInteractiveServiceSession()) return undefined;

    if (typeof window.requestIdleCallback !== 'function') {
        const timer = setTimeout(startRequest, PREFETCH_IDLE_TIMEOUT_MS);
        return () => clearTimeout(timer);
    }

    const handle = window.requestIdleCallback(startRequest, { timeout: PREFETCH_IDLE_TIMEOUT_MS });
    return () => window.cancelIdleCallback(handle);
};

const usePromptSuggestions = () => {
    const [state, setState] = useState(() =>
        settledResult === undefined
            ? { suggestions: null, isLoading: true }
            : { suggestions: settledResult, isLoading: false }
    );

    useEffect(() => {
        let cancelled = false;
        startRequest().then(result => {
            if (!cancelled) setState({ suggestions: result, isLoading: false });
        });

        return () => {
            cancelled = true;
        };
    }, []);

    return state;
};

export default usePromptSuggestions;
